import * as React from 'react'
import { Formik, Form, Field, FieldProps } from 'formik'
import * as Yup from 'yup'
import { Button, Input, Select, Textarea } from '@mobiscroll/react-latest'
import { IStationUserGoinZoneObject, GoinZoneType } from 'src/types'
import UIMessages from 'src/utils/ui-messages'

interface IServiceZonesProps {
  onCancel: () => void
  color: string
  onAddServiceZone: (values: any) => Promise<any>
  specificErrorMsg: string
  buffer: any[]
  showPallete: boolean
  showSpecificError: (msg: string) => void
  onClickColorPick: () => void
  getColorPallete: () => any
  toEdit: IStationUserGoinZoneObject | null
  showMessage: (message: string, isError: boolean) => void
}

interface IServiceZonesState {
  loading: boolean
  statusMessage: string
}

class ServiceZones extends React.Component<IServiceZonesProps, IServiceZonesState> {
  constructor(props: any) {
    super(props)
    this.state = {
      loading: false,
      statusMessage: ''
    }
  }

  public render() {
    return <div>{this.getAddServiceZonePane()}</div>
  }

  private getAddServiceZonePane = () => {
    const {
      cancel,
      color,
      add,
      description: descriptionLabel,
      save,
      title: titleLabel,
      zoneType: zoneTypeLabel
    } = UIMessages.common

    const inputProps = {
      inputStyle: 'box' as const,
      labelStyle: 'floating' as const,
      theme: 'ios'
    }

    const addServiceZoneSchema = Yup.object().shape({
      zoneTitle: Yup.string()
        .min(2, 'Must be longer than 2 characters')
        .required('Title is required'),
      description: Yup.string()
        .min(2, 'Must be longer than 2 characters')
        .required('Description is required')
    })

    const zoneOptions = Object.keys(GoinZoneType).map(k => {
      return {
        text: GoinZoneType[k],
        value: GoinZoneType[k]
      }
    })

    const onSubmit = (values: any) => {
      this.props.showSpecificError(this.props.buffer.length === 0 ? 'You must select an area' : '')
      if (this.props.buffer.length === 0) {
        return
      }
      this.setState({
        loading: true,
        statusMessage: this.props.toEdit ? 'Saving...' : 'Adding...'
      })

      this.props
        .onAddServiceZone({
          ...values,
          mapObjectType: 'service',
          original: this.props.toEdit
        })
        .then(
          () => {
            this.setState({
              loading: false,
              statusMessage: ''
            })
          },
          err => {
            this.props.showSpecificError('Failed to add service zone: ' + err)
            this.setState({
              loading: false,
              statusMessage: ''
            })
          }
        )
    }

    const bodyStyle: any = {
      pointerEvents: this.state.loading ? 'none' : 'auto',
      opacity: this.state.loading ? 0.4 : 1.0
    }

    const render = ({ errors, touched, setFieldValue, setFieldTouched }: any) => {
      const { description: descriptionErr, zoneTitle: zoneTitleErr } = errors

      const { description: descriptionTouched, zoneTitle: zoneTitleTouched } = touched

      const textInput = ({ field, form }: FieldProps<any>) => {
        const inputDetails = {
          zoneTitle: {
            error: zoneTitleErr && zoneTitleTouched,
            errorMessage: zoneTitleErr,
            label: `${titleLabel}*`
          }
        }
        const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
        return <Input {...field} {...inputProps} {...textInputProps} />
      }

      const textAreaInput = ({ field, form }: FieldProps<any>) => {
        const inputDetails = {
          description: {
            error: descriptionErr && descriptionTouched,
            errorMessage: descriptionErr,
            label: `${descriptionLabel}*`,
            rows: 5
          }
        }
        const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
        return <Textarea {...field} {...inputProps} {...textInputProps} style={{ minHeight: '120px' }} />
      }

      const selectInput = ({ field, form }: FieldProps<any>) => {
        const onChangeWrap = (value: string) => {
          setFieldTouched(field.name)
          setFieldValue(field.name, value)
        }
        const inputDetails = {
          zoneType: {
            data: zoneOptions,
            label: `${zoneTypeLabel}*`,
            required: true
          }
        }
        const selectProps = inputDetails[field.name as keyof typeof inputDetails]

        return <Select {...field} {...inputProps} {...selectProps} onChange={({ value }) => onChangeWrap(value)} />
      }

      return (
        <Form>
          <div style={bodyStyle}>
            <div className='mbsc-row'>
              <div className='mbsc-col-12 mbsc-padding row-gap-16'>
                <div className='flex'>
                  <div style={{ flex: 1 }}>
                    <Field name='zoneTitle' render={textInput} />
                  </div>
                  <div className='flex flex-column items-center ml1'>
                    <div className='h5'>{color}</div>
                    <div
                      onClick={this.props.onClickColorPick}
                      className='rounded'
                      style={{
                        cursor: 'pointer',
                        display: 'inline-block',
                        width: '24px',
                        height: '24px',
                        backgroundColor: this.props.color
                      }}
                    >
                      &nbsp;
                    </div>
                  </div>
                </div>
                {this.props.showPallete && <div className='pt2'>{this.props.getColorPallete()}</div>}
              </div>
              <div className='mbsc-col-12 mbsc-padding'>
                <Field name='description' render={textAreaInput} />
              </div>
              <div className='mbsc-col-12 mbsc-padding'>
                <Field name='zoneType' render={selectInput} />
              </div>
              {!!this.props.specificErrorMsg.length && (
                <div className='mbsc-col-12'>
                  {this.props.specificErrorMsg.length > 0 && (
                    <div className='font-small weight-300 font-red'>{this.props.specificErrorMsg}</div>
                  )}
                </div>
              )}
              <div className='mbsc-col-12 mbsc-padding'>
                <div className='flex justify-end'>
                  <Button ariaLabel='Cancel button' className='mbsc-flat-btn' onClick={this.props.onCancel}>
                    {cancel}
                  </Button>
                  <Button ariaLabel='Save button' className='mbsc-success-btn' type='submit'>
                    {this.props.toEdit ? save : add}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Form>
      )
    }

    return (
      <div className='mb3'>
        <Formik
          initialValues={{
            zoneTitle: this.props.toEdit ? this.props.toEdit.name : '',
            description: this.props.toEdit ? this.props.toEdit.description : '',
            price: this.props.toEdit ? this.props.toEdit.price : 0,
            zoneType: this.props.toEdit ? this.props.toEdit.zoneType : GoinZoneType.SERVICE_ZONE
          }}
          validationSchema={addServiceZoneSchema}
          onSubmit={onSubmit}
          render={render}
        />
      </div>
    )
  }
}

export default ServiceZones



// WEBPACK FOOTER //
// ./src/components/station-user/view/ServiceZones.tsx